// Procedure type mapping
export const PROCEDURE_MAP = {
  "Brazilian butt lift (BBL)": "BBL",
  BBL: "BBL",
  "Tummy Tuck (TT)": "TUMMY_TUCK",
  ETT: "TUMMY_TUCK",
  "Mini Tummy Tuck": "TUMMY_TUCK",
  "Fat Transfer Breast Augmentation": "BREAST_AUGMENTATION",
  "Fat Transf Breast": "BREAST_AUGMENTATION",
  "Breast Augmentation": "BREAST_AUGMENTATION",
  "Breast Augmentation (Silicone)": "BREAST_AUGMENTATION",
  "BAM Silicone": "BREAST_LIFT_AND_AUGMENTATION",
  "Breast Augmentation (Saline)": "BREAST_AUGMENTATION",
  "BAM Saline": "BREAST_LIFT_AND_AUGMENTATION",
  "BA Silicone": "BREAST_AUGMENTATION",
  "Breast Lift": "BREAST_LIFT",
  "Breast Lift (with implant)": "BREAST_LIFT",
  "Breast Lift (no implant)": "BREAST_LIFT",
  "BL no implant": "BREAST_LIFT",
  "Breast Reduction": "BREAST_REDUCTION",
  "Breast Reduc": "BREAST_REDUCTION",
  Rhinoplasty: "RHINOPLASTY",
  "Chin Liposuction": "CHIN_LIPOSUCTION",
  CHIN_LIPOSUCTION: "CHIN_LIPOSUCTION",
  Facelift: "FACELIFT",
  Liposuction: "LIPOSUCTION",
  Lipo: "LIPOSUCTION",
  "Mommy Makeover": "MOMMY_MAKEOVER",
  LIPOSUCTION: "LIPOSUCTION",
};

// API endpoints
// Set the API URL based on the environment
const isStaging = () => {
  if (typeof window !== "undefined") {
    const currentUrl = window.location.href.toLowerCase();
    return (
      currentUrl.includes("localhost") ||
      currentUrl.includes("staging") ||
      currentUrl.includes("dev.")
    );
  }
};

const API_BASE_URL = isStaging()
  ? "https://api-staging.clinicos.ai"
  : "https://api.clinicos.ai";

/**
 * Get a presigned URL for image upload - now with enhanced debugging
 * @param {TSession} session - The session object containing accessToken
 * @param {string} sourceUrl - Source URL of the image
 * @returns {Promise<object|null>} Presigned URL data or null on failure
 */
export async function processImageWithSession(session, sourceUrl) {
  if (!session || !session.accessToken) {
    console.error(`DEBUG - MISSING SESSION OR ACCESS TOKEN`);
    return null;
  }

  if (!session.clinicId) {
    console.error(`DEBUG - MISSING CLINIC ID IN SESSION`);
    return null;
  }

  const clinicId = session.clinicId;
  console.log(`DEBUG - Using clinic ID: ${clinicId}`);

  // Get presigned URL for uploading
  const presignedData = await getPresignedUrlForImage(session, sourceUrl);
  if (!presignedData) {
    console.error(`DEBUG - Failed to get presigned URL`);
    return null;
  }

  // Upload the image
  const success = await uploadImageToPresignedUrl(
    sourceUrl,
    presignedData.presignedUrl,
  );
  if (!success) {
    console.error(`DEBUG - Failed to upload image to S3`);
    return null;
  }

  return presignedData;
}

/**
 * Gets a presigned URL for image upload using the session
 * @param {TSession} session - The session object
 * @param {string} sourceUrl - The image URL or data URL to process
 * @returns {Promise<object|null>} Presigned URL data or null on failure
 */
export async function getPresignedUrlForImage(session, sourceUrl) {
  const clinicId = session.clinicId;
  const accessToken = session.accessToken;

  const apiUrl = `${API_BASE_URL}/clinics/v1/side-by-side/${clinicId}/before-after/upload`;

  const payload = {
    num_before_images: 1,
    num_after_images: 0,
    timestamp: Math.floor(Date.now() / 1000),
  };

  try {
    const headers = {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    };

    console.log(`DEBUG - Request headers:`, {
      Authorization: `Bearer ${accessToken.substring(0, 5)}...`,
      "Content-Type": "application/json",
    });

    const response = await fetch(apiUrl, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });

    console.log(
      `DEBUG - Presigned URL response status: ${response.status} ${response.statusText}`,
    );

    if (!response.ok) {
      // Attempt to get response body for more detailed error information
      let errorBody = "";
      try {
        errorBody = await response.text();
        console.error(
          `DEBUG - Presigned URL error response body: ${errorBody}`,
        );

        // Try to parse as JSON for better logging
        try {
          const errorJson = JSON.parse(errorBody);

          // Log specific clinic-user relationship details if available
          if (errorJson.error_code === "CLINICUSER__INVALID_RELATIONSHIP") {
            const errorMsg = errorJson.message;

            // Extract IDs from error message if possible
            const userIdMatch = errorMsg.match(/user_id=([^,]+)/);
            const clinicIdMatch = errorMsg.match(/clinic_id=([^}]+)/);
          }
        } catch (e) {
          // Not JSON, just use the text
          console.error(`DEBUG - Error body not JSON: ${errorBody}`);
        }
      } catch (textError) {
        console.error(
          `DEBUG - Failed to get error response text: ${textError.message}`,
        );
      }

      console.error(
        `DEBUG - Failed to get presigned URL: ${response.status} ${response.statusText}`,
      );
      return null;
    }

    const data = await response.json();

    if (
      data &&
      data.before_input_images &&
      data.before_input_images.length > 0
    ) {
      const inputImage = data.before_input_images[0];
      return {
        presignedUrl: data.presigned_put_urls_before_images[0],
        s3Url: inputImage.url,
        s3Key: inputImage.s3_key,
        batchUuid: data.batch_uuid,
      };
    }

    return null;
  } catch (error) {
    return null;
  }
}

/**
 * Upload an image to the presigned URL
 * @param {string|Blob} input - Source URL, data URL, or blob of the image
 * @param {string} presignedUrl - Presigned URL for uploading
 * @returns {Promise<boolean>} Success status
 */
export async function uploadImageToPresignedUrl(input, presignedUrl) {
  try {
    // Download image to get a blob
    const imageBlob = await downloadImage(input);
    const contentType = imageBlob.type || "image/jpeg";

    // Upload to the presigned URL
    const uploadResponse = await fetch(presignedUrl, {
      method: "PUT",
      headers: {
        "Content-Type": contentType,
      },
      body: imageBlob,
    });

    if (!uploadResponse.ok) {
      try {
        const errorText = await uploadResponse.text();
        console.error(`DEBUG - Upload error response: ${errorText}`);
      } catch (textError) {
        console.error(
          `DEBUG - Failed to get upload error text: ${textError.message}`,
        );
      }
      return false;
    }

    console.log(`DEBUG - Successfully uploaded image to S3`);
    return true;
  } catch (error) {
    console.error(
      `DEBUG - Error in uploadImageToPresignedUrl: ${error.message}`,
    );
    console.error(`DEBUG - Error stack: ${error.stack}`);
    return false;
  }
}

export async function transformImage(
  session,
  imageUrl,
  procedureType,
  clinicId,
  patientId,
) {
  console.log(`DEBUG - transformImage called`);

  try {
    if (!patientId) {
      console.error(
        `DEBUG - CRITICAL: No patient ID provided for transform API`,
      );
      return null;
    }

    // Map the procedure type
    const mappedProcedureType = PROCEDURE_MAP[procedureType] || procedureType;

    // Construct the transforms URL with clinic_id and patient_id
    const url = new URL(
      `${API_BASE_URL}/clinics/v1/clinic/${clinicId}/patients/${patientId}/transforms`,
    );
    url.searchParams.append("source", "dashboard");
    const transformsUrl = url.toString();

    const payload = {
      image_url: imageUrl,
      procedure_types: [mappedProcedureType],
      no_frame_interpolation: true,
    };

    console.log(`DEBUG - Transform payload: ${JSON.stringify(payload)}`);

    const response = await fetch(transformsUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${session.accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    console.log(
      `DEBUG - Transform response status: ${response.status} ${response.statusText}`,
    );

    if (!response.ok) {
      let errorText = "";
      try {
        errorText = await response.text();
        console.error(`DEBUG - Transform error response: ${errorText}`);
      } catch (e) {
        console.error(
          `DEBUG - Failed to get transform error text: ${e.message}`,
        );
      }
      return null;
    }

    const data = await response.json();

    return data;
  } catch (error) {
    return null;
  }
}

/**
 * Monitor the status of a simulation
 * @param {string} metadataUrl - The metadata URL to poll
 * @param {number} maxAttempts - Maximum number of polling attempts
 * @param {number} initialDelay - Initial delay in milliseconds
 * @returns {Promise<object>} The final status response
 */
export async function checkSimulationStatus(
  metadataUrl,
  maxAttempts = 15,
  initialDelay = 3000,
) {
  console.log(`DEBUG - Polling metadata URL: ${metadataUrl}`);

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const delay = initialDelay * Math.pow(1.4, attempt); // Exponential backoff

    // Wait before checking status
    await new Promise((resolve) => setTimeout(resolve, delay));

    try {
      const response = await fetch(metadataUrl);

      console.log(
        `DEBUG - Status check response: ${response.status} ${response.statusText}`,
      );

      if (!response.ok) {
        if (response.status === 404 && attempt < maxAttempts - 1) {
          console.log(
            `DEBUG - Metadata not found yet (404), continuing poll...`,
          );
          continue;
        }

        console.warn(`DEBUG - Unexpected status response: ${response.status}`);

        if (attempt === maxAttempts - 1) {
          throw new Error(
            `DEBUG - Status check failed: ${response.status} ${response.statusText}`,
          );
        }
        continue;
      }

      const data = await response.json();
      console.log(`DEBUG - Status check data received`);

      if (data && data.status) {
        if (data.status.success === true) {
          console.log(`DEBUG - Simulation status: Success`);
          return data; // Return the complete data on success
        }

        if (data.status.success === false || data.status.error) {
          console.error(
            `DEBUG - Simulation status: Failed - ${data.status.error || "Unknown error"}`,
          );
          throw new Error(
            `DEBUG - Simulation failed: ${data.status.error || "Unknown API error"}`,
          );
        }

        console.log(`DEBUG - Simulation status: Still processing...`);
      } else {
        console.warn(`DEBUG - Unexpected status response structure`);
      }
    } catch (error) {
      console.error(
        `DEBUG - Error checking simulation status (Attempt ${attempt + 1}): ${error.message}`,
      );

      if (attempt === maxAttempts - 1) {
        throw new Error(
          `DEBUG - Status check failed after ${maxAttempts} attempts: ${error.message}`,
        );
      }
    }
  }

  throw new Error(
    `DEBUG - Status check timed out after ${maxAttempts} attempts`,
  );
}

// To Avoid CORS Errors

/**
 * Download an image and return it as a blob, handling CORS issues
 * @param {string|Blob} input - URL, data URL, or blob
 * @returns {Promise<Blob>} - Image as blob
 */
const downloadImage = async (input) => {
  // If input is already a blob, just return it
  if (input instanceof Blob || input instanceof File) {
    console.log(`DEBUG - Using provided blob directly`);
    return input;
  }

  // If input is a data URL, convert it to blob
  if (typeof input === "string" && input.startsWith("data:")) {
    console.log(`DEBUG - Converting data URL to blob`);
    const parts = input.split(",");
    const matches = parts[0].match(/data:([^;]+);base64/);
    const contentType =
      matches && matches.length > 1 ? matches[1] : "application/octet-stream";
    const base64 = parts[1];
    const binary = atob(base64);
    const array = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      array[i] = binary.charCodeAt(i);
    }
    return new Blob([array], { type: contentType });
  }

  // Otherwise, try to download the image using createObjectURL approach
  // This avoids CORS issues when possible
  try {
    console.log(
      `DEBUG - Downloading image using fetch with credentials: ${input.substring(0, 30)}...`,
    );

    console.log(input);

    // First try with credentials included
    const response = await fetch(input, {
      credentials: "include",
      mode: "cors",
      cache: "no-cache",
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch image: ${response.status} ${response.statusText}`,
      );
    }

    return await response.blob();
  } catch (error) {
    // If fetch fails, try the canvas approach as a fallback
    console.warn(
      `DEBUG - Fetch failed (${error.message}), trying canvas approach`,
    );

    // Create an image and canvas
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";

      img.onload = () => {
        // Create canvas and draw image to it
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);

        // Convert to blob
        canvas.toBlob(
          (blob) => {
            resolve(blob);
          },
          "image/jpeg",
          0.95,
        );
      };

      img.onerror = () => {
        reject(new Error("Failed to load image with canvas approach"));
      };

      img.src = input;
    });
  }
};

// Starring Images

// Add these functions to your simulation service

/**
 * Toggle star status for a simulation image
 * @param {Object} session - User session with auth token
 * @param {string} clinicId - UUID of the clinic
 * @param {number} imageId - ID of the image to star/unstar
 * @param {boolean} isStarred - Whether to star (true) or unstar (false) the image
 * @returns {Promise<boolean>} - Success status
 */
const toggleSimulationStar = async (session, clinicId, imageId, isStarred) => {
  try {
    if (!session || !session.accessToken) {
      console.error("No valid session provided");
      return false;
    }

    const response = await fetch(
      `${API_BASE_URL}/clinics/v1/clinic/${clinicId}/images/star`,
      {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${session.accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          image_id: imageId,
          is_starred: !isStarred,
        }),
      },
    );

    if (response.status === 204) {
      console.log(
        `✅ Successfully ${isStarred ? "starred" : "unstarred"} image ${imageId}`,
      );
      return true;
    } else {
      const errorText = await response.text();
      console.error(
        `❌ Failed to ${isStarred ? "star" : "unstar"} image ${imageId}: ${response.status} - ${errorText}`,
      );
      return false;
    }
  } catch (error) {
    console.error(`❌ Error in toggleSimulationStar:`, error);
    return false;
  }
};

/**
 * Get all starred simulation images for a patient
 * @param {Object} session - User session with auth token
 * @param {string} clinicId - UUID of the clinic
 * @param {string} patientId - ID of the patient
 * @returns {Promise<Array>} - Array of starred images
 */
const getStarredSimulationImages = async (session, clinicId, patientId) => {
  try {
    if (!session || !session.accessToken) {
      console.error("No valid session provided");
      return [];
    }

    const response = await fetch(
      `${API_BASE_URL}/clinics/v1/clinic/${clinicId}/patients/${patientId}/images?is_starred=true`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${session.accessToken}`,
        },
      },
    );

    if (response.status === 200) {
      const data = await response.json();
      const images = data.images || [];
      return images;
    } else {
      const errorText = await response.text();
      console.error(
        `❌ Failed to fetch starred images: ${response.status} - ${errorText}`,
      );
      return [];
    }
  } catch (error) {
    console.error(`❌ Error in getStarredSimulationImages:`, error);
    return [];
  }
};

// Utility functions

export const filterSupportedProcedures = (procedureTypes) => {
  if (!procedureTypes || !Array.isArray(procedureTypes)) return [];
  return procedureTypes
    .map((proc) => cleanProcedureString(proc))
    .filter((proc) => proc)
    .filter((proc) => isProcedureSupported(proc));
};

export const cleanProcedureString = (procedureString) => {
  if (!procedureString || typeof procedureString !== "string") return "";
  return procedureString.replace(/,/g, "").trim();
};

export const isProcedureSupported = (procedureString) => {
  if (!procedureString || typeof procedureString !== "string") return false;
  const parts = procedureString.split(",").map((part) => part.trim());
  for (const part of parts) {
    if (PROCEDURE_MAP.hasOwnProperty(part)) {
      return true;
    }
  }
  return false;
};

export const getFirstSupportedProcedure = (procedureString) => {
  if (!procedureString || typeof procedureString !== "string") return null;
  const parts = procedureString.split(",").map((part) => part.trim());
  for (const part of parts) {
    if (PROCEDURE_MAP.hasOwnProperty(part)) {
      return part;
    }
  }
  return null;
};

export default {
  processImageWithSession,
  getPresignedUrlForImage,
  uploadImageToPresignedUrl,
  transformImage,
  checkSimulationStatus,
  isProcedureSupported,
  filterSupportedProcedures,
  cleanProcedureString,
  getFirstSupportedProcedure,
  PROCEDURE_MAP,
  toggleSimulationStar,
  getStarredSimulationImages,
};
